import { useState, type ReactElement } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { current_language } from '../i18n/index.js';
import { orpc } from '../lib/orpc.js';
import { BulkImportModal } from '../components/BulkImportModal.js';
import { Icon } from '../components/Icon.js';
import { friendly_error } from '../lib/friendly-error.js';

interface Chit {
  id: string;
  name_ta: string;
  name_en: string;
  pool_size: number;
  duration_months: number;
  monthly_subscription_paise: number;
  current_month_pointer?: number;
  status: string;
}

type StatusFilter = 'all' | 'active' | 'completed' | 'draft';

const HEALTH_DOT: Record<string, string> = {
  draft: 'watch',
  active: 'good',
  completed: 'good',
  cancelled: 'bad',
};

// Status pill — colour-coded label shown on every chit card so the operator
// can scan publish state at a glance even when the All filter is active.
const STATUS_PILL: Record<string, { ta: string; en: string; bg: string; fg: string }> = {
  draft: { ta: 'வரைவு', en: 'Draft', bg: 'var(--warn-soft)', fg: 'var(--warn)' },
  active: { ta: 'வெளியிடப்பட்டது', en: 'Published', bg: 'var(--good-soft)', fg: 'var(--good)' },
  completed: { ta: 'முடிந்தது', en: 'Completed', bg: 'var(--bg-elev)', fg: 'var(--ink-3)' },
  cancelled: { ta: 'ரத்து', en: 'Cancelled', bg: 'var(--bad-soft)', fg: 'var(--bad)' },
};

// paise → compact "₹X.XL" / "₹X.X Cr" for the header total.
function compact_inr(paise: number): string {
  const r = paise / 100;
  if (r >= 1e7) return `₹${(r / 1e7).toFixed(1)} Cr`;
  if (r >= 1e5) return `₹${(r / 1e5).toFixed(1)} L`;
  return `₹${r.toLocaleString('en-IN')}`;
}
function value_l(paise: number): string {
  const l = paise / 100 / 1e5;
  return `₹${l >= 10 ? l.toFixed(0) : l.toFixed(1)}L`;
}
function monthly_k(paise: number): string {
  const k = paise / 100 / 1000;
  return k >= 1 ? `₹${k.toFixed(0)}k` : `₹${(paise / 100).toLocaleString('en-IN')}`;
}

export function ChitsPage(): ReactElement {
  const lang = current_language();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [bulk_open, set_bulk_open] = useState(false);
  const [filter, set_filter] = useState<StatusFilter>('all');
  const q = useQuery({ queryKey: ['chits'], queryFn: () => orpc.chits.list({}) });
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  const chits = (q.data?.chits ?? []) as Chit[];
  const count = (s: StatusFilter): number =>
    s === 'all' ? chits.length : chits.filter((c) => c.status === s).length;
  const active_value = chits
    .filter((c) => c.status === 'active')
    .reduce((s, c) => s + c.monthly_subscription_paise * c.pool_size, 0);
  const shown = filter === 'all' ? chits : chits.filter((c) => c.status === filter);

  const CHIPS: Array<{ id: StatusFilter; ta: string; en: string }> = [
    { id: 'all', ta: 'அனைத்தும்', en: 'All' },
    { id: 'active', ta: 'வெளியிடப்பட்டது', en: 'Published' },
    { id: 'completed', ta: 'முடிந்தது', en: 'Completed' },
    { id: 'draft', ta: 'வரைவு', en: 'Draft' },
  ];

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('சீட்டு குழுக்கள்', 'Chit Groups')}</div>
          <div className="h-sub">
            {count('active')} {t('வெளியிடப்பட்டது', 'published')} · {compact_inr(active_value)} {t('நிர்வாகத்தில்', 'under management')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => set_bulk_open(true)}>
            <Icon.download /> {t('இறக்குமதி', 'Bulk import')}
          </button>
          <button className="btn primary" onClick={() => nav('/chits/new')}>
            <Icon.plus /> {t('புதிய சீட்டு', 'New Chit')}
          </button>
        </div>
      </div>

      {bulk_open && (
        <BulkImportModal
          kind="chits"
          on_close={() => set_bulk_open(false)}
          on_done={() => void qc.invalidateQueries({ queryKey: ['chits'] })}
        />
      )}

      <div className="filters" style={{ marginBottom: 14, borderRadius: 8, border: '1px solid var(--line)' }}>
        {CHIPS.map((c) => (
          <button
            key={c.id}
            className={'chip' + (filter === c.id ? ' on' : '')}
            onClick={() => set_filter(c.id)}
          >
            {lang === 'ta' ? c.ta : c.en} <span className="ct">{count(c.id)}</span>
          </button>
        ))}
      </div>

      {q.isLoading && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>}
      {q.error && <div style={{ color: 'var(--bad)', fontSize: 13 }}>{friendly_error(q.error)}</div>}
      {q.data && shown.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 32 }}>
          {filter === 'all'
            ? t('இன்னும் சீட்டுகள் இல்லை', 'No chits yet')
            : filter === 'active'
              ? t('இன்னும் வெளியிடப்பட்ட சீட்டுகள் இல்லை', 'No published chits yet')
              : t('இந்த நிலையில் சீட்டுகள் இல்லை', 'No chits in this status')}
        </div>
      )}

      {shown.length > 0 && (
        <div className="chits-page-grid">
          {shown.map((c) => {
            const value = c.monthly_subscription_paise * c.pool_size;
            const current = c.current_month_pointer ?? 0;
            const pct = c.duration_months > 0 ? Math.min((current / c.duration_months) * 100, 100) : 0;
            return (
              <div
                key={c.id}
                className="chit-card-2"
                onClick={() => nav(`/chits/${c.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="chit-head">
                  <span className={'health-dot ' + (HEALTH_DOT[c.status] ?? 'good')} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>
                      {lang === 'ta' ? c.name_ta : c.name_en}
                    </div>
                    <div className="chit-id">{c.id.slice(0, 8)} · {c.duration_months}m</div>
                  </div>
                  {(() => {
                    const p = STATUS_PILL[c.status];
                    if (!p) return null;
                    return (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '3px 8px',
                          borderRadius: 999,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          whiteSpace: 'nowrap',
                          background: p.bg,
                          color: p.fg,
                        }}
                      >
                        {lang === 'ta' ? p.ta : p.en}
                      </span>
                    );
                  })()}
                </div>
                <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  <div className="stat" style={{ padding: '6px 8px' }}>
                    <div className="lbl">{t('சீட்டு மதிப்பு', 'Value')}</div>
                    <div className="val" style={{ fontSize: 14 }}>{value_l(value)}</div>
                  </div>
                  <div className="stat" style={{ padding: '6px 8px' }}>
                    <div className="lbl">{t('மாதம்', 'Monthly')}</div>
                    <div className="val" style={{ fontSize: 14 }}>{monthly_k(c.monthly_subscription_paise)}</div>
                  </div>
                  <div className="stat" style={{ padding: '6px 8px' }}>
                    <div className="lbl">{t('உறுப்பினர்', 'Members')}</div>
                    <div className="val" style={{ fontSize: 14 }}>{c.pool_size}</div>
                  </div>
                </div>
                <div>
                  <div className="progress"><span style={{ width: pct + '%' }} /></div>
                  <div className="progress-meta">
                    <span>{t('கட்டம்', 'Instalment')} {current}/{c.duration_months}</span>
                    <span>{c.duration_months} {t('மாதம்', 'months')}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
